import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import type { DOMAttributes } from 'react';
import { CheckoutContext } from '@/context/CheckoutContext';
import { FeatureFlag } from '@/constants/featureFlags';
import { trackPayCtaPulseRendered } from '@/utils/checkoutMixpanelTracking';

/**
 * Idle delay before the pulse re-arms after an interaction ends. Long
 * enough to feel intentional (the user is "done touching") and short
 * enough that an inattentive user gets re-cued quickly. Tuned to match
 * the Figma reference behavior on Sprint 87.
 */
const PULSE_IDLE_RESUME_MS = 1500;

/**
 * Subset of `DOMAttributes` we need to drive the pulse's pause/resume
 * cycle. Kept narrow so callers can't accidentally treat this as an
 * "all-handlers" object — the consumer remains responsible for its own
 * `onClick`, focus-trap, accessibility wiring, etc.
 */
type PulseInteractionHandlers = Pick<
  DOMAttributes<HTMLElement>,
  | 'onMouseEnter'
  | 'onMouseLeave'
  | 'onMouseDown'
  | 'onMouseUp'
  | 'onTouchStart'
  | 'onTouchEnd'
  | 'onTouchCancel'
  | 'onFocus'
  | 'onBlur'
>;

interface UsePayCtaIdlePulseResult {
  /** Spread on the element whose visual state should reflect the pulse. */
  isPulsing: boolean;
  /** Spread on the SAME element as `isPulsing` is read on. */
  interactionHandlers: PulseInteractionHandlers;
}

/**
 * Pay-CTA idle pulse — Sprint 87 (`payCtaIdlePulse` flag).
 *
 * AC mapping:
 *  - scope:     consumed exactly once, by `<PayButton>` (the primary
 *               "Pay" CTA). Wallet buttons (Apple/Google/PayPal/Venmo)
 *               are out of scope and untouched by this hook.
 *  - behavior:  pulse runs only while `isActionable` AND the user is
 *               not currently interacting (hover, focus, touch). After
 *               interaction ends, a 1500ms idle timer re-arms the
 *               pulse — prevents flicker if the user briefly leaves
 *               and returns.
 *  - cancel:    when the caller flips `isActionable` to false (loading
 *               kicks in, form goes invalid, caller disables) we cancel
 *               any pending idle-resume so the pulse doesn't flash back
 *               on the moment the button becomes actionable again. Also
 *               resets `isInteracting` so the next actionable transition
 *               starts pulsing immediately.
 *  - analytics: `pay_cta_pulse_rendered` fires ONCE per session, the
 *               first time the pulse becomes visible. Module-scoped
 *               guard in `checkoutMixpanelTracking.ts` ensures we
 *               survive re-mounts (template fallbacks, etc.).
 *  - reduced-motion: handled in CSS (`prefers-reduced-motion: reduce`
 *               sets `animation: none`); the class still applies but
 *               renders no motion. Nothing to do here.
 *
 * Cost when the flag is OFF (production default until BE flips it):
 *   - one boolean lookup per render
 *   - the returned handlers early-return after one boolean check, so
 *     every pointer / focus event on the CTA costs ~nanoseconds and
 *     never touches state.
 *   - `isPulsing` is `false`, so the caller never applies the CSS
 *     class and the animation never runs.
 */
export const usePayCtaIdlePulse = ({ isActionable }: { isActionable: boolean }): UsePayCtaIdlePulseResult => {
  const context = useContext(CheckoutContext);
  const isPulseEnabled = Boolean(context.featureFlags?.[FeatureFlag.PAY_CTA_IDLE_PULSE]);

  const [isInteracting, setIsInteracting] = useState(false);
  const idleResumeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearIdleResumeTimer = useCallback(() => {
    if (idleResumeTimerRef.current !== null) {
      clearTimeout(idleResumeTimerRef.current);
      idleResumeTimerRef.current = null;
    }
  }, []);

  const beginInteraction = useCallback(() => {
    if (!isPulseEnabled) return;
    clearIdleResumeTimer();
    setIsInteracting(true);
  }, [isPulseEnabled, clearIdleResumeTimer]);

  const endInteraction = useCallback(() => {
    if (!isPulseEnabled) return;
    clearIdleResumeTimer();
    idleResumeTimerRef.current = setTimeout(() => {
      idleResumeTimerRef.current = null;
      setIsInteracting(false);
    }, PULSE_IDLE_RESUME_MS);
  }, [isPulseEnabled, clearIdleResumeTimer]);

  useEffect(() => clearIdleResumeTimer, [clearIdleResumeTimer]);

  useEffect(() => {
    if (!isActionable) {
      clearIdleResumeTimer();
      setIsInteracting(false);
    }
  }, [isActionable, clearIdleResumeTimer]);

  const isPulsing = isPulseEnabled && isActionable && !isInteracting;

  const pulseRenderedRef = useRef(false);
  useEffect(() => {
    if (isPulsing && !pulseRenderedRef.current) {
      pulseRenderedRef.current = true;
      trackPayCtaPulseRendered(context);
    }
  }, [isPulsing, context]);

  return {
    isPulsing,
    interactionHandlers: {
      onMouseEnter: beginInteraction,
      onMouseLeave: endInteraction,
      onMouseDown: beginInteraction,
      // Without onMouseUp, a plain desktop click (mousedown→mouseup without
      // leaving the button) would leave `isInteracting` stuck true and the
      // pulse would never re-arm. Pair it with onMouseDown so the idle-resume
      // timer starts as soon as the click completes.
      onMouseUp: endInteraction,
      onTouchStart: beginInteraction,
      onTouchEnd: endInteraction,
      onTouchCancel: endInteraction,
      onFocus: beginInteraction,
      onBlur: endInteraction,
    },
  };
};
